import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';

/**
 * Skeleton — placeholder de carga con "shimmer" para el tema dark "torre de control".
 *
 * Reemplaza al spinner centrado por un bloque que insinua la forma del contenido que
 * esta por llegar (percepcion de velocidad). El color base es `surface-raised` (un
 * escalon por encima de `surface`/`bg`): un placeholder de bajo contraste que no compite
 * con el contenido real. El brillo es un barrido diagonal translucido animado por CSS.
 *
 * Movimiento: la duracion sale de los tokens `--pvp-duration-*` (barrido lento). Ante
 * `prefers-reduced-motion: reduce` NO se anima — se rinde un bloque estatico (la capa
 * de shimmer ni siquiera se monta), doble guardia via `@media` en los estilos.
 *
 * Formas (`variant`):
 *   - `text`   linea de texto (rounded-sm); con `rows` > 1 apila varias lineas, la ultima
 *              mas corta (imita un parrafo). Alto por defecto 0.8em.
 *   - `block`  bloque rectangular (rounded-md) para cards/graficos.
 *   - `circle` avatar/icono (rounded-full, cuadrado por defecto).
 *
 * Dimensiones: `width`/`height` aceptan numero (=> px) o string CSS ('60%', '2rem'...).
 *
 * a11y: por defecto es decorativo (`aria-hidden="true"`) — se usa dentro de contenedores
 * que ya comunican el estado de carga (p.ej. el `pvp-data-table-shell`). Con `announce`
 * pasa a `role="status"` + `aria-busy="true"` + label para uso aislado.
 */
export type SkeletonVariant = 'text' | 'block' | 'circle';

@Component({
  selector: 'pvp-skeleton',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '[attr.role]': "announce() ? 'status' : null",
    '[attr.aria-busy]': "announce() ? 'true' : null",
    '[attr.aria-label]': 'announce() ? label() : null',
    '[attr.aria-hidden]': "announce() ? null : 'true'",
  },
  template: `
    @if (variant() === 'text' && rows() > 1) {
      <span class="flex flex-col gap-2">
        @for (row of rowsArray(); track $index) {
          <span
            class="pvp-skeleton pvp-skeleton--text"
            [style.width]="lineWidth($index)"
            [style.height]="resolvedHeight()"
          >
            @if (!reducedMotion()) {
              <span class="pvp-skeleton__shimmer" aria-hidden="true"></span>
            }
          </span>
        }
      </span>
    } @else {
      <span
        [class]="blockClass()"
        [style.width]="resolvedWidth()"
        [style.height]="resolvedHeight()"
      >
        @if (!reducedMotion()) {
          <span class="pvp-skeleton__shimmer" aria-hidden="true"></span>
        }
      </span>
    }
  `,
  styles: [
    `
      :host {
        --pvp-skeleton-sweep: calc(var(--pvp-duration-slow, 320ms) * 5);
      }

      .pvp-skeleton {
        position: relative;
        display: block;
        overflow: hidden;
        background: var(--pvp-color-surface-raised);
        border-radius: var(--pvp-radius-md, 10px);
      }

      .pvp-skeleton--text {
        border-radius: var(--pvp-radius-sm, 6px);
      }

      .pvp-skeleton--circle {
        border-radius: 9999px;
      }

      /* Barrido de brillo: una franja clara translucida que cruza el bloque.
         El contraste es deliberadamente bajo (tema dark) para no distraer. */
      .pvp-skeleton__shimmer {
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(232, 238, 234, 0.06) 50%,
          transparent 100%
        );
        animation: pvp-skeleton-shimmer var(--pvp-skeleton-sweep) ease-in-out infinite;
      }

      @keyframes pvp-skeleton-shimmer {
        100% {
          transform: translateX(100%);
        }
      }

      /* Doble guardia: aunque la capa de shimmer no se monta en reduced-motion,
         se corta cualquier animacion por si el bloque llega a renderizarse. */
      @media (prefers-reduced-motion: reduce) {
        .pvp-skeleton__shimmer {
          animation: none;
          display: none;
        }
      }
    `,
  ],
})
export class SkeletonComponent {
  readonly variant = input<SkeletonVariant>('text');
  /** Numero (=> px) o string CSS ('60%', '3rem'). Por defecto 100% (text/block) o 40px (circle). */
  readonly width = input<string | number | null>(null);
  /** Numero (=> px) o string CSS. Por defecto 0.8em (text), 100% (block) o 40px (circle). */
  readonly height = input<string | number | null>(null);
  /** Cantidad de lineas para `variant="text"` (parrafo). La ultima queda mas corta. */
  readonly rows = input(1);
  /** Activa `role="status"` + `aria-busy` para uso aislado (por defecto es decorativo). */
  readonly announce = input(false);
  readonly label = input('Cargando...');

  /** Se lee en construccion y se mantiene reactivo; el listener se limpia al destruir. */
  protected readonly reducedMotion = signal(false);

  constructor() {
    const mql =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    if (mql) {
      this.reducedMotion.set(mql.matches);
      const onChange = (e: MediaQueryListEvent): void => this.reducedMotion.set(e.matches);
      mql.addEventListener?.('change', onChange);
      inject(DestroyRef).onDestroy(() => mql.removeEventListener?.('change', onChange));
    }
  }

  protected readonly rowsArray = computed(() => Array.from({ length: Math.max(1, this.rows()) }));

  protected readonly blockClass = computed(() => {
    const variantClass: Record<SkeletonVariant, string> = {
      text: 'pvp-skeleton pvp-skeleton--text',
      block: 'pvp-skeleton',
      circle: 'pvp-skeleton pvp-skeleton--circle',
    };
    return variantClass[this.variant()];
  });

  protected readonly resolvedWidth = computed(() => {
    const explicit = this.toCss(this.width());
    if (explicit !== null) return explicit;
    return this.variant() === 'circle' ? '40px' : '100%';
  });

  protected readonly resolvedHeight = computed(() => {
    const explicit = this.toCss(this.height());
    if (explicit !== null) return explicit;
    if (this.variant() === 'circle') return '40px';
    if (this.variant() === 'block') return '100%';
    return '0.8em';
  });

  /** Ultima linea mas corta para que un parrafo no parezca un bloque perfecto. */
  protected lineWidth(index: number): string {
    const base = this.toCss(this.width()) ?? '100%';
    const isLast = index === this.rowsArray().length - 1;
    return isLast && this.rowsArray().length > 1 ? '62%' : base;
  }

  private toCss(value: string | number | null): string | null {
    if (value === null) return null;
    return typeof value === 'number' ? `${value}px` : value;
  }
}
